import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Building2, Plus, Search, Eye, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { formatUkDate } from "@/lib/date";

export default function AdminClients() {
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [companyQuery, setCompanyQuery] = useState("");
  const [companyResults, setCompanyResults] = useState([]);
  const [companyBusy, setCompanyBusy] = useState(false);
  const [clientTypes, setClientTypes] = useState(DEFAULT_CLIENT_TYPES);
  const [serviceCatalog, setServiceCatalog] = useState([]);
  const nav = useNavigate();

  const [form, setForm] = useState(emptyClientForm());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/clients", { params: q ? { q } : {} });
      setClients(data);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [q]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    let active = true;
    Promise.all([
      api.get("/admin/accountancy/client-types"),
      api.get("/admin/accountancy/services"),
    ]).then(([typesResponse, servicesResponse]) => {
      if (!active) return;
      const types = typesResponse.data?.client_types;
      const services = servicesResponse.data?.services;
      if (Array.isArray(types) && types.length) {
        const storedKeys = new Set(types.map((type) => type.key));
        setClientTypes([...types, ...DEFAULT_CLIENT_TYPES.filter((type) => !storedKeys.has(type.key))]);
      }
      if (Array.isArray(services)) setServiceCatalog(services);
    }).catch(() => {
      // The form remains usable with the complete local entity defaults.
    });
    return () => { active = false; };
  }, []);
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("create") !== "1") return;
    setOpen(true);
    params.delete("create");
    const query = params.toString();
    window.history.replaceState(null, "", `${window.location.pathname}${query ? `?${query}` : ""}`);
  }, []);

  async function onCreate(e) {
    e.preventDefault();
    if (contacts.length > 0 && !form.main_contact_name) {
      toast.error("Select the main contact before creating the client");
      return;
    }
    setBusy(true);
    try {
      const selectedType = clientTypes.find((type) => type.key === form.client_type);
      const suggestedKeys = Array.isArray(selectedType?.service_keys) ? selectedType.service_keys : [];
      const suggestedServices = serviceCatalog.filter((service) => suggestedKeys.includes(service.key));
      const serviceSettings = Object.fromEntries(suggestedServices.map((service) => [service.key, {
        enabled: true,
        fee: "",
        start_date: service.start_date || "",
      }]));
      const { accountancy_enabled, ...profile } = form;
      await api.post("/admin/clients", {
        ...profile,
        sales_autoentry_email: form.sales_autoentry_email || null,
        native_accounting_enabled: accountancy_enabled && form.accounting_destination === "native",
        module_settings: JSON.stringify({ accountancy: { enabled: accountancy_enabled } }),
        service_settings: JSON.stringify(serviceSettings),
        services_required: suggestedServices.map((service) => service.label).join("\n"),
      });
      toast.success("Client created");
      setOpen(false);
      setForm(emptyClientForm());
      setCompanyQuery("");
      setCompanyResults([]);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  const totalOutstanding = useMemo(
    () => clients.reduce((s, c) => s + (c.purchase_outstanding || 0) + (c.sales_outstanding || 0), 0),
    [clients]
  );

  async function searchCompaniesHouse() {
    if (!companyQuery.trim()) return;
    setCompanyBusy(true);
    try {
      const { data } = await api.get("/admin/companies-house/search", { params: { q: companyQuery.trim() } });
      setCompanyResults(data || []);
      if (!data?.length) toast.info("No Companies House matches found");
    } catch (e) {
      toast.error(formatApiError(e), { duration: 9000 });
    } finally {
      setCompanyBusy(false);
    }
  }

  async function importCompany(company) {
    const basicProfile = {
      business_name: company.title || "",
      client_type: company.company_type === "ltd" ? "limited_company" : (company.company_type || ""),
      company_number: company.company_number || "",
      company_status: company.company_status || "",
      incorporation_date: company.date_of_creation || "",
      registered_office_address: company.address || "",
      main_contact_name: "",
      main_contact_role: "",
      company_directors: "",
      company_pscs: "",
      company_contacts: "",
      companies_house_filings: "",
    };
    setForm((current) => ({ ...mergeCompanyProfile(current, basicProfile), ...basicProfile }));
    setCompanyBusy(true);
    try {
      const { data } = await api.get(`/admin/companies-house/profile/${company.company_number}`);
      setForm((current) => mergeCompanyProfile(current, data));
      toast.success("Companies House details added");
    } catch {
      toast.warning("Company selected, but Companies House contact details could not be loaded. You can enter the contact manually.", { duration: 9000 });
    } finally {
      setCompanyBusy(false);
    }
  }

  async function deleteClient(client) {
    if (!window.confirm(`Delete ${client.business_name}? This removes the client account and cannot be undone.`)) return;
    try {
      await api.delete(`/admin/clients/${client._id}`);
      toast.success("Client deleted");
      await load();
    } catch (error) {
      toast.error(formatApiError(error));
    }
  }

  const contacts = parseStoredList(form.company_contacts);

  function selectContactAsMain(contact) {
    const names = splitPersonName(contact?.name);
    setForm((current) => ({
      ...current,
      first_name: names.first_name || current.first_name,
      last_name: names.last_name || current.last_name,
      main_contact_name: contact?.name || current.main_contact_name,
      main_contact_role: contact?.role || contact?.kind || current.main_contact_role,
    }));
  }

  return (
    <div className="space-y-4">
      <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold tracking-tight text-stone-900">Client settings</h1>
          <p className="text-sm text-stone-600">
            {clients.length} {clients.length === 1 ? "client" : "clients"} · {totalOutstanding} outstanding items total
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="h-9 gap-2" style={{ background: "var(--brand)" }} data-testid="add-client-btn">
              <Plus className="h-4 w-4" /> New client
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[92vh] overflow-y-auto sm:max-w-4xl">
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">Create client account</DialogTitle>
            </DialogHeader>
            <form onSubmit={onCreate} className="space-y-4" data-testid="create-client-form">
              <div className="rounded-md border border-stone-200 bg-stone-50/70 p-3">
                <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-stone-900">
                  <Building2 className="h-4 w-4 text-[var(--brand)]" /> Companies House lookup
                </div>
                <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                  <Input value={companyQuery} onChange={(e) => setCompanyQuery(e.target.value)} placeholder="Search company name or number" className="h-9 bg-white" />
                  <Button type="button" variant="outline" onClick={searchCompaniesHouse} disabled={companyBusy} className="gap-2">
                    <Search className="h-4 w-4" /> Search
                  </Button>
                </div>
                {companyResults.length > 0 && (
                  <div className="mt-2 max-h-48 overflow-auto rounded-md border border-stone-200 bg-white">
                    {companyResults.map((company) => (
                      <button
                        key={company.company_number}
                        type="button"
                        onClick={() => importCompany(company)}
                        className={`flex w-full items-start justify-between gap-3 border-b border-stone-100 px-3 py-2 text-left text-sm last:border-b-0 hover:bg-stone-50 ${form.company_number === company.company_number ? "bg-emerald-50 ring-1 ring-inset ring-emerald-300" : ""}`}
                      >
                        <span>
                          <span className="block font-semibold text-stone-900">{company.title}</span>
                          <span className="block text-xs text-stone-500">{company.company_number} - {company.address || "No address shown"}</span>
                        </span>
                        <span className="flex items-center gap-2"><Badge variant="secondary">{company.company_status}</Badge>{form.company_number === company.company_number && <Badge className="bg-emerald-100 text-emerald-800">Selected</Badge>}</span>
                      </button>
                    ))}
                  </div>
                )}
                {form.company_number && (
                  <div className="mt-2 grid gap-2 text-xs sm:grid-cols-3">
                    <Summary label="Company no." value={form.company_number} />
                    <Summary label="Status" value={form.company_status} />
                    <Summary label="Incorporated" value={formatUkDate(form.incorporation_date)} />
                  </div>
                )}
                {contacts.length > 0 && (
                  <div className="mt-2">
                    <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-stone-500">Choose main contact <span className="normal-case tracking-normal text-red-600">(required)</span></div>
                    <div className="grid gap-2 sm:grid-cols-2">
                      {contacts.slice(0, 10).map((contact, index) => (
                        <button
                          key={`${contact.name}-${index}`}
                          type="button"
                          onClick={() => selectContactAsMain(contact)}
                          className={`rounded-md border px-2.5 py-2 text-left text-xs font-semibold hover:border-emerald-300 hover:bg-emerald-50 ${form.main_contact_name === contact.name ? "border-emerald-400 bg-emerald-50 text-emerald-900" : "border-stone-200 bg-white text-stone-700"}`}
                        >
                          <span className="block">{contact.name} <span className="font-normal text-stone-500">{contact.role || contact.kind}</span></span>
                          {contact.address && <span className="mt-1 block font-normal text-stone-500">{contact.address}</span>}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <Field label="First name" id="first_name" value={form.first_name} onChange={(v)=>setForm({...form, first_name: v})} testid="client-first-name" />
                <Field label="Last name" id="last_name" value={form.last_name} onChange={(v)=>setForm({...form, last_name: v})} testid="client-last-name" />
              </div>
              <Field label="Business name" id="business_name" value={form.business_name} onChange={(v)=>setForm({...form, business_name: v})} testid="client-business-name" />
              <Field label="Login email" id="email" type="email" value={form.email} onChange={(v)=>setForm({...form, email: v})} testid="client-email" />
              <Field label="Email for purchase invoices" id="autoentry_email" type="email" value={form.autoentry_email} onChange={(v)=>setForm({...form, autoentry_email: v})} testid="client-autoentry-email" />
              <Field label="Email for sales invoices (optional)" id="sales_autoentry_email" type="email" value={form.sales_autoentry_email} onChange={(v)=>setForm({...form, sales_autoentry_email: v})} required={false} testid="client-sales-autoentry-email" />
              <Field label="Initial password" id="password" type="password" value={form.password} onChange={(v)=>setForm({...form, password: v})} testid="client-password" />
              <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
                <div className="mb-3">
                  <div className="text-sm font-semibold text-stone-900">Accountancy setup</div>
                  <p className="mt-0.5 text-xs text-stone-600">Classify the client now so the correct services, statutory checks and filing safeguards are available from the first save.</p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <SelectField label="Client type" value={form.client_type} onChange={(value) => setForm({ ...form, client_type: value })} options={clientTypes.map((type) => [type.key, type.label])} testid="client-type" />
                  <SelectField label="Industry" value={form.industry} onChange={(value) => setForm({ ...form, industry: value })} options={INDUSTRIES.map((value) => [value, value])} testid="client-industry" />
                  <SelectField label="Accounting destination" value={form.accounting_destination} onChange={(value) => setForm({ ...form, accounting_destination: value })} options={[["native", "EPOS native accounting"], ["external", "External accounting software"]]} testid="client-accounting-destination" />
                  <SelectField label="Statutory filing route" value={form.statutory_submission_route} onChange={(value) => setForm({ ...form, statutory_submission_route: value })} options={[["external_submission", "Filed externally — simulate and retain evidence"], ["direct_submission", "Direct to authority — connected service required"]]} testid="client-filing-route" />
                </div>
                <label className="mt-3 flex items-start gap-3 rounded-lg border border-stone-200 bg-white p-3 text-sm">
                  <input type="checkbox" checked={form.accountancy_enabled} onChange={(event) => setForm({ ...form, accountancy_enabled: event.target.checked })} className="mt-1 h-4 w-4" data-testid="client-accountancy-module" />
                  <span><span className="font-semibold text-stone-800">Enable accountancy module</span><span className="block text-stone-500">Creates the accounting workspace and applies the services suggested for this client type. You can amend them from the client profile after creation.</span></span>
                </label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Company number" id="company_number" value={form.company_number} onChange={(v)=>setForm({...form, company_number: v})} required={false} />
                <Field label="Main contact role" id="main_contact_role" value={form.main_contact_role} onChange={(v)=>setForm({...form, main_contact_role: v})} required={false} />
              </div>
              <Field label="Registered office address" id="registered_office_address" value={form.registered_office_address} onChange={(v)=>setForm({...form, registered_office_address: v})} required={false} />
              <Field label="Trading address (optional)" id="trading_address" value={form.trading_address} onChange={(v)=>setForm({...form, trading_address: v})} required={false} />
              <label className="flex items-start gap-3 rounded-xl border border-stone-200 bg-stone-50/60 p-3 text-sm">
                <input
                  type="checkbox"
                  checked={form.is_vat_client}
                  onChange={(e) => setForm({ ...form, is_vat_client: e.target.checked })}
                  className="mt-1 h-4 w-4"
                  data-testid="client-vat-client"
                />
                <span>
                  <span className="font-semibold text-stone-800">VAT client</span>
                  <span className="block text-stone-500">Ask the document check to look for VAT invoice details.</span>
                </span>
              </label>
              <label className="flex items-start gap-3 rounded-xl border border-stone-200 bg-stone-50/60 p-3 text-sm">
                <input
                  type="checkbox"
                  checked={form.ai_analysis_enabled}
                  onChange={(e) => setForm({ ...form, ai_analysis_enabled: e.target.checked })}
                  className="mt-1 h-4 w-4"
                  data-testid="client-ai-analysis"
                />
                <span>
                  <span className="font-semibold text-stone-800">AI analysis</span>
                  <span className="block text-stone-500">Run invoice photos through AI review before emailing.</span>
                </span>
              </label>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpen(false)} data-testid="cancel-create-btn">Cancel</Button>
                <Button type="submit" disabled={busy} style={{ background: "var(--brand)" }} data-testid="submit-create-btn">
                  {busy ? "Creating…" : "Create client"}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </header>

      <div className="relative max-w-md">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
        <Input
          placeholder="Search by business, name or email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="h-9 pl-10"
          data-testid="client-search"
        />
      </div>

      {clients.length === 0 ? (
        <div className="border border-dashed border-stone-300 rounded-md bg-white p-10 text-center" data-testid="empty-clients">
          <p className="font-display text-xl text-stone-700">No clients yet</p>
          <p className="text-sm text-stone-500 mt-2">Add your first client to start uploading their outstanding invoices.</p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-4 gap-3">
          {clients.map((c) => (
            <article
              key={c._id}
              className="group flex min-h-[220px] flex-col rounded-xl border border-stone-200 bg-white p-4 text-left shadow-[0_3px_12px_rgba(28,25,23,0.07)] transition duration-150 hover:-translate-y-0.5 hover:border-emerald-300 hover:shadow-[0_10px_26px_rgba(6,78,59,0.13)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-600 focus-visible:ring-offset-2"
              data-testid={`client-card-${c._id}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex min-w-0 items-start gap-3">
                  <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-100">
                    <Building2 className="h-6 w-6" />
                  </span>
                  <div className="min-w-0 pt-0.5">
                    <div className="truncate font-display text-base font-bold leading-tight text-stone-950">{c.business_name}</div>
                    <div className="mt-1 truncate text-xs text-stone-500">{c.first_name} {c.last_name}</div>
                    <div className="mt-1.5 text-[10px] font-bold uppercase tracking-wide text-emerald-700">Practice client</div>
                  </div>
                </div>
                <Badge variant={c.status === "active" ? "default" : "secondary"} className={c.status === "active" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : ""}>
                  {c.status}
                </Badge>
              </div>
              <div className="mt-3 min-h-6">
                {c.is_vat_client && <Badge className="bg-sky-100 text-sky-800 hover:bg-sky-100">VAT client</Badge>}
                {c.ai_analysis_enabled && <Badge className="ml-2 bg-violet-100 text-violet-800 hover:bg-violet-100">AI analysis</Badge>}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 border-t border-stone-200 pt-3">
                <Stat label="Purchase" value={c.purchase_outstanding} />
                <Stat label="Sales" value={c.sales_outstanding} />
              </div>
              <div className="mt-auto border-t border-stone-100 pt-3 text-sm">
                <span className="block truncate text-xs text-stone-500">{c.email}</span>
                <div className="mt-2 flex flex-wrap gap-2">
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => nav(`/admin/clients/${c._id}?mode=view`)}><Eye className="h-3.5 w-3.5" /> View</Button>
                  <Button size="sm" variant="outline" className="gap-1.5" onClick={() => nav(`/admin/clients/${c._id}`)}><Pencil className="h-3.5 w-3.5" /> Edit</Button>
                  <Button size="sm" variant="outline" className="ml-auto gap-1.5 text-red-700 hover:bg-red-50" onClick={() => deleteClient(c)}><Trash2 className="h-3.5 w-3.5" /> Delete</Button>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

function Field({ label, id, value, onChange, type = "text", required = true, testid }) {
  return (
    <div>
      <Label htmlFor={id} className="text-sm font-semibold text-stone-700">{label}</Label>
      <Input id={id} type={type} required={required} value={value} onChange={(e) => onChange(e.target.value)} className="mt-1 h-9" data-testid={testid} />
    </div>
  );
}

function SelectField({ label, value, onChange, options, testid }) {
  return (
    <label className="block text-sm font-semibold text-stone-700">
      {label}
      <select required value={value} onChange={(event) => onChange(event.target.value)} className="mt-1 h-9 w-full rounded-md border border-stone-300 bg-white px-3 text-sm font-normal text-stone-900" data-testid={testid}>
        <option value="">Select</option>
        {options.map(([optionValue, optionLabel]) => <option key={optionValue} value={optionValue}>{optionLabel}</option>)}
      </select>
    </label>
  );
}

function Summary({ label, value }) {
  return (
    <div className="rounded-md border border-stone-200 bg-white px-2.5 py-2">
      <div className="text-[10px] font-semibold uppercase tracking-wide text-stone-500">{label}</div>
      <div className="truncate text-sm font-semibold text-stone-900">{value || "-"}</div>
    </div>
  );
}

function Stat({ label, value }) {
  const empty = !value;
  return (
    <div className={`rounded-md px-3 py-2 border ${empty ? "bg-stone-50 border-stone-200" : "border-amber-200"}`}
      style={!empty ? { background: "var(--outstanding-bg)" } : undefined}>
      <div className="text-[11px] uppercase tracking-wider font-semibold text-stone-500">{label}</div>
      <div className={`font-display text-xl font-bold ${empty ? "text-stone-400" : ""}`}
        style={!empty ? { color: "var(--outstanding)" } : undefined}>{value || 0}</div>
    </div>
  );
}

function emptyClientForm() {
  return {
    first_name: "",
    last_name: "",
    business_name: "",
    email: "",
    autoentry_email: "",
    sales_autoentry_email: "",
    password: "",
    status: "active",
    is_vat_client: false,
    ai_analysis_enabled: false,
    client_type: "limited_company",
    industry: "Professional, scientific and technical",
    accountancy_enabled: true,
    accounting_destination: "native",
    statutory_submission_route: "external_submission",
    company_number: "",
    company_status: "",
    incorporation_date: "",
    registered_office_address: "",
    trading_address: "",
    phone: "",
    utr: "",
    vat_number: "",
    paye_reference: "",
    accounts_office_reference: "",
    authorisation_codes: "",
    services_required: "",
    service_settings: "",
    statutory_deadlines: "",
    deadline_tasks: "",
    bookkeeping_frequency: "",
    payroll_frequency: "",
    year_end: "",
    practice_manager: "",
    companies_house_last_checked: "",
    main_contact_name: "",
    main_contact_role: "",
    company_directors: "",
    company_pscs: "",
    company_contacts: "",
    companies_house_filings: "",
  };
}

const DEFAULT_CLIENT_TYPES = [
  { key: "limited_company", label: "Limited company", service_keys: ["accounts", "bookkeeping", "ct600_return", "corporation_tax_payment", "confirmation_statement"] },
  { key: "sole_trader", label: "Sole trader", service_keys: ["bookkeeping", "self_assessment", "self_assessment_payment", "payment_on_account"] },
  { key: "partnership", label: "Partnership", service_keys: ["bookkeeping", "partnership_return"] },
  { key: "llp", label: "LLP", service_keys: ["accounts", "bookkeeping", "partnership_return", "confirmation_statement"] },
  { key: "charity", label: "Charity", service_keys: ["accounts", "bookkeeping", "charity_annual_return"] },
  { key: "community_interest_company", label: "CIC", service_keys: ["accounts", "bookkeeping", "ct600_return", "corporation_tax_payment", "confirmation_statement", "cic34_report"] },
  { key: "club_or_association", label: "Club / association", service_keys: ["accounts", "bookkeeping"] },
  { key: "cis_customer", label: "CIS customer", service_keys: ["bookkeeping", "cis", "self_assessment", "self_assessment_payment", "payment_on_account"] },
  { key: "landlord", label: "Landlord", service_keys: ["bookkeeping", "self_assessment", "self_assessment_payment", "payment_on_account"] },
  { key: "individual", label: "Individual", service_keys: ["self_assessment", "self_assessment_payment", "payment_on_account"] },
  { key: "trust_or_estate", label: "Trust / estate", service_keys: ["bookkeeping"] },
  { key: "other", label: "Other", service_keys: [] },
];

const INDUSTRIES = [
  "Accommodation and food services", "Administrative and support services", "Agriculture, forestry and fishing",
  "Arts, entertainment and recreation", "Construction", "Education", "Financial and insurance activities",
  "Health and social work", "Information and communication", "Manufacturing", "Motor trade",
  "Professional, scientific and technical", "Property and real estate", "Retail and wholesale",
  "Transport and storage", "Utilities", "Other service activities",
];

function mergeCompanyProfile(current, data) {
  const imported = Object.fromEntries(Object.entries(data || {}).filter(([, value]) => value != null && value !== ""));
  const next = { ...current, ...imported };
  if (current.first_name || current.last_name) {
    next.first_name = current.first_name;
    next.last_name = current.last_name;
  } else if (imported.main_contact_name) {
    const names = splitPersonName(imported.main_contact_name);
    next.first_name = names.first_name;
    next.last_name = names.last_name;
  }
  next.email = current.email;
  next.autoentry_email = current.autoentry_email;
  next.sales_autoentry_email = current.sales_autoentry_email;
  next.password = current.password;
  return next;
}

function parseStoredList(value) {
  if (Array.isArray(value)) return value;
  if (!value) return [];
  try {
    const parsed = JSON.parse(value);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function splitPersonName(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return { first_name: parts[0] || "", last_name: "" };
  return { first_name: parts.slice(0, -1).join(" "), last_name: parts[parts.length - 1] };
}
